import { MutableRefObject, useMemo, useState } from 'react';

/**
 * Custom drop-in `useRef` implementation which causes re-render and can be used as effects dependencies.
 *
 * @param initialValue - initial ref value
 * @returns
 *
 * @see https://developer.mozilla.org/en-US/docs/Web/JavaScript/Guide/Working_with_Objects#defining_getters_and_setters
 * @see https://github.com/facebook/react/blob/2fba484cd095ea79b940364cea5107fa4ca9f0c8/packages/react-reconciler/src/ReactFiberHooks.js#L2231-L2268
 *
 * @example
 * ```
 * const ref = useRefState(0);
 * ref.current = 1; // ref.current is now 1 and wherever the value is used will re-render
 * console.log(ref.current); // 1
 * ```
 *
 * WARNINGS (08/30/2023):
 * Whenever the state changes, the whole ref object is recreated instead of just the `current` property.
 * This means both of these would re-render when `ref.current` is reassigned:
 * ```
 * const ref = useRefState();
 *
 * useEffect(()=>{
 *  myLogics()
 * },[ref])
 *
 * useEffect(()=>{
 *  myLogics()
 * },[ref.current])
 * ```
 */
export function useRefState<TValue = any>(initialValue?: TValue): MutableRefObject<TValue> {
  const [value, setValue] = useState<TValue>(initialValue as TValue);

  return useMemo(
    () => ({
      get current() {
        return value;
      },
      set current(newValue: TValue) {
        setValue(newValue);
      },
    }),
    [value, setValue],
  );
}
